<template>
  <div class="admin-tools">
    <h1>Users</h1>
    <p class="intro">Find a member, update their profile, and manage their verification status and newsletter preference.</p>
    <form class="toolbar" @submit.prevent="loadUsers(true)">
      <input v-model="search" aria-label="Search users" placeholder="Search name, username, or email" maxlength="150" />
      <button type="submit" :disabled="loading">{{ loading ? 'Searching…' : 'Search' }}</button>
    </form>
    <div v-if="error" class="notice error" role="alert">{{ error }}</div>
    <div v-if="message" class="notice success" role="status">{{ message }}</div>
    <div class="workspace">
      <section class="panel" aria-label="User results">
        <p v-if="!users.length && !loading" class="muted">No matches on this page. Try another search or continue through the results.</p>
        <button v-for="user in users" :key="user.id" class="record" :class="{ selected: selected?.id === user.id }" :disabled="busy" @click="select(user)">
          {{ user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username }}
          <span v-if="user.status === 'verified'" class="badge"><vue-feather type="check-circle" size="14" /> Verified</span>
          <small>@{{ user.username }} · {{ user.email }}</small>
        </button>
        <button v-if="nextToken" :disabled="loading" @click="loadUsers(false)">{{ loading ? 'Loading…' : 'Load more users' }}</button>
      </section>
      <section class="panel" aria-label="Edit user">
        <p v-if="!selected" class="muted">Select a user to edit their profile.</p>
        <template v-else>
          <h2>@{{ selected.username }}</h2>
          <p class="muted">{{ selected.email }} · {{ selected.emailVerified ? 'Email verified' : 'Email not verified' }}</p>
          <form @submit.prevent="save">
            <label>Display name<input v-model="form.displayName" maxlength="150" /></label>
            <div class="fields">
              <label>First name<input v-model="form.firstName" maxlength="100" /></label>
              <label>Last name<input v-model="form.lastName" maxlength="100" /></label>
            </div>
            <label>Bio<textarea v-model="form.bio" rows="3" maxlength="2000" /></label>
            <label>Country<input v-model="form.country" maxlength="100" /></label>
            <label>Profile status<select v-model="form.status"><option value="verified">Verified</option><option value="unverified">Unverified</option><option value="banned">Banned</option></select></label>
            <p class="muted">Verified channels show a badge, appear in channel listings, and publish uploads without review. It does not change email verification or grant admin access. Banned profiles are excluded from campaigns and public search; this does not disable account sign-in.</p>
            <div class="actions"><button class="primary" :disabled="busy">{{ busy ? 'Saving…' : 'Save profile' }}</button><router-link :to="`/@${selected.username}`">View profile</router-link></div>
          </form>
          <div class="divider">
            <h2>Newsletter preference</h2>
            <p v-if="subscriptionError" class="notice" role="status">{{ subscriptionError }}</p>
            <template v-else>
            <p>{{ optedIn ? 'Subscribed to announcements' : 'Not subscribed to announcements' }}</p>
            <p v-if="consent?.recordedAt" class="muted">Recorded {{ new Date(consent.recordedAt).toLocaleDateString() }}: {{ consent.source }}</p>
            <template v-if="!optedIn">
              <label>Consent record<textarea v-model="consentSource" rows="2" maxlength="500" placeholder="Where and when this person agreed to receive announcements" /></label>
              <label class="check"><input v-model="consentConfirmed" type="checkbox" /> I have confirmed this person’s consent.</label>
            </template>
            <button :disabled="busy || (!optedIn && (!consentConfirmed || !consentSource.trim()))" @click="saveConsent">{{ optedIn ? 'Unsubscribe from announcements' : 'Record consent and subscribe' }}</button>
            </template>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>
<script>
import { adminRequest, adminError } from '../utils/adminConsole';
export default {
  name: 'AdminUsers',
  data: () => ({ search: '', activeSearch: '', users: [], nextToken: null, loading: false, busy: false, error: '', message: '', selected: null, form: {}, optedIn: false, consent: null, consentSource: '', consentConfirmed: false, subscriptionError: '', requestId: 0 }),
  mounted() { this.loadUsers(true); },
  methods: {
    async loadUsers(reset) {
      if (this.loading) return;
      this.loading = true; this.error = '';
      if (reset) { this.activeSearch = this.search; this.nextToken = null; }
      try {
        const result = await adminRequest('users', { search: this.activeSearch, nextToken: this.nextToken });
        this.users = reset ? result.items : [...this.users, ...result.items];
        this.nextToken = result.nextToken;
      } catch (error) { this.error = adminError(error); }
      finally { this.loading = false; }
    },
    async select(user) {
      if (this.busy) return;
      const requestId = ++this.requestId;
      this.busy = true; this.error = ''; this.message = '';
      try {
        const result = await adminRequest('user', { id: user.id });
        if (requestId !== this.requestId) return;
        this.selected = result.user;
        this.form = Object.fromEntries(['firstName', 'lastName', 'displayName', 'bio', 'country'].map(key => [key, result.user[key] || '']));
        this.form.status = result.user.status || 'unverified';
        this.optedIn = result.subscribed; this.consent = result.consent;
        this.subscriptionError = result.subscriptionError || '';
        this.consentSource = ''; this.consentConfirmed = false;
      } catch (error) { this.error = adminError(error); }
      finally { this.busy = false; }
    },
    async save() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const user = await adminRequest('updateUser', { id: this.selected.id, ...this.form }, true);
        this.selected = user;
        this.users = this.users.map(item => item.id === user.id ? user : item);
        this.message = 'Profile saved.';
      } catch (error) { this.error = adminError(error); }
      finally { this.busy = false; }
    },
    async saveConsent() {
      this.busy = true; this.error = ''; this.message = '';
      try {
        const result = await adminRequest('consent', { id: this.selected.id, subscribed: !this.optedIn, source: this.consentSource }, true);
        this.optedIn = result.subscribed; this.consentConfirmed = false;
        this.message = this.optedIn ? 'Consent recorded. This user can receive announcements.' : 'Unsubscribed from announcements.';
      } catch (error) { this.error = adminError(error); }
      finally { this.busy = false; }
    }
  }
};
</script>
<style src="../styles/admin-tools.css"></style>
